import React, { useState } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { Close, EmojiEvents } from '@mui/icons-material';
import VisitLeaderboard from './VisitLeaderboard';

/**
 * Right-side collapsible tab + drawer for the visit-tracking leaderboard.
 * Closed by default — only visible once someone clicks the tab. Purely
 * presentational: owns no data-fetching of its own, just shows/hides
 * <VisitLeaderboard /> (which keeps its own independent loading/error
 * state). Added 2026-08-25.
 */
export default function VisitLeaderboardPanel() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Only rendered while the drawer is closed. It used to render
          unconditionally at zIndex drawer+1, which put it ABOVE the open
          drawer's own paper too — it sat fixed over the table (visibly
          overlapping row content) instead of disappearing once open, and
          its onClick only ever called setOpen(true), so it did nothing
          useful while already open anyway. Fixed 2026-09-11. */}
      {!open ? (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: '45%',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <IconButton
            onClick={() => setOpen(true)}
            aria-label="Open leaderboard"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '8px 0 0 8px',
              boxShadow: 2,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <EmojiEvents />
          </IconButton>
        </Box>
      ) : null}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            // Wider fixed panel (was 400, then 560) so user names/emails and
            // longer durations like "22h 55m" have room to breathe without
            // truncating, and 3-digit visit counts (e.g. "999") sit comfortably.
            // Kept as a plain fixed width, not resizable. 2026-09-04: bumped
            // again after "71h ..." was still truncating at 560.
            width: { xs: '100%', sm: 640 },
            maxWidth: '100vw',
          },
        }}
      >
        <Box sx={{ p: 2, overflowX: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6">Leaderboard</Typography>
            <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close leaderboard">
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          <VisitLeaderboard />
        </Box>
      </Drawer>
    </>
  );
}
